import { Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { ChipSelect, EmptyState, Loading, SessionCard } from '../components';
import { scoreSessionCompatibility } from '../matching';
import { Link } from '../router';
import { useApp } from '../store';
import type { SessionSummary } from '../types';

export function Sessions() {
  const { games, profile, authStatus } = useApp();
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [gameFilter, setGameFilter] = useState<string[]>([]);
  const [tab, setTab] = useState<'upcoming' | 'mine'>('upcoming');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.sessions({ game: gameFilter[0], scope: tab })
      .then((data) => alive && setSessions(data.sessions))
      .catch(() => alive && setSessions([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [gameFilter, tab]);

  const ordered = useMemo(() => {
    if (!profile || tab === 'mine') return sessions;
    return [...sessions].sort((a, b) => scoreSessionCompatibility(profile, b).score - scoreSessionCompatibility(profile, a).score);
  }, [sessions, profile, tab]);

  return (
    <div className="page">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Find your squad</span>
          <h1>Sessions</h1>
        </div>
        {authStatus === 'authed'
          ? <Link to="/sessions/new" className="primary small"><Plus size={15} /> Host a session</Link>
          : <Link to="/signup" className="secondary small">Sign up to host</Link>}
      </div>

      {authStatus === 'authed' && (
        <div className="tab-row" role="tablist" aria-label="Session lists">
          <button role="tab" aria-selected={tab === 'upcoming'} className={`tab ${tab === 'upcoming' ? 'active' : ''}`} onClick={() => { setLoading(true); setTab('upcoming'); }}>Open sessions</button>
          <button role="tab" aria-selected={tab === 'mine'} className={`tab ${tab === 'mine' ? 'active' : ''}`} onClick={() => { setLoading(true); setTab('mine'); }}>My sessions</button>
        </div>
      )}

      <ChipSelect
        options={[{ value: '', label: 'All games' }, ...games.map((game) => ({ value: game.slug, label: game.name }))]}
        selected={gameFilter.length ? gameFilter : ['']}
        onToggle={(value) => { setLoading(true); setGameFilter(value ? [value] : []); }}
        single
      />

      {loading ? (
        <Loading label="Finding sessions…" />
      ) : ordered.length === 0 ? (
        tab === 'mine' ? (
          <EmptyState
            title="No sessions yet"
            hint="Sessions you join or host show up here, including your play history."
            action={<Link to="/sessions/new" className="primary small">Host a session</Link>}
          />
        ) : (
          <EmptyState
            title="No upcoming sessions"
            hint={gameFilter.length ? 'Nothing scheduled for this game yet. Host the first one.' : 'Nothing scheduled yet. Host a session and let compatible players find you.'}
            action={authStatus === 'authed' ? <Link to="/sessions/new" className="primary small">Host a session</Link> : <Link to="/signup" className="primary small">Create account</Link>}
          />
        )
      ) : (
        <div className="session-grid">
          {ordered.map((session, index) => (
            <SessionCard key={session.id} session={session} profile={profile} featured={tab === 'upcoming' && index === 0 && Boolean(profile)} />
          ))}
        </div>
      )}
    </div>
  );
}
